import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Col,
  Spinner,
} from "reactstrap";
import { SquarePlus } from "lucide-react";
import axios from "axios";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import ClassRoomCard from "./ClassRoomCard";
import { API_BASE_URL } from "../../constants"; // Make sure it's defined
import { Toast } from "primereact/toast";

const ClassRooms = () => {
  const [classes, setClasses] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [roomNumber, setRoomNumber] = useState("");
  const [capacity, setCapacity] = useState("");
  const [selectedBuilding, setSelectedBuilding] = useState("");
  const [buildingName, setBuildingName] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalCenter, setModalCenter] = useState(false);
  const [buildingModal, setBuildingModal] = useState(false);
  const [capacityError, setCapacityError] = useState("");
  const [editRoomNumber, setEditRoomNumber] = useState("");
  const [editClassroomId, setEditClassroomId] = useState("");
  const [editCapacity, setEditCapacity] = useState("");
  const [editSelectedBuilding, setEditSelectedBuilding] = useState("");
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [classroomToDelete, setClassroomToDelete] = useState(null);

  const toast = useRef(null);

  // Toggle Edit Modal
  const toggleEditModal = (classroom) => {
    setEditClassroomId(classroom.id);
    setEditRoomNumber(classroom.room_number);
    setEditCapacity(classroom.total_strength);
    setEditSelectedBuilding(classroom.building_id);
    setEditModal(!editModal);
  };

  // Toggle Add Room Modal
  const toggleModal = () => setModalCenter((prev) => !prev);

  // Toggle Delete Confirmation Modal
  const toggleDeleteModal = (classroom = null) => {
    setClassroomToDelete(classroom); // Set the classroom to delete
    setDeleteModal((prev) => !prev); // Toggle the modal
  };

  // Toggle Building Modal
  const toggleBuildingModal = () => {
    setBuildingModal((prev) => !prev);
    setBuildingName("");
  };

  // Fetch Building Data
  const fetchBuildingData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/admin/buildings/list`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setBuildings(data?.data || []);
    } catch (error) {
      if (error.response) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error.response.data?.message || "Failed to fetch buildings",
          life: 3000,
        });
      } else {
        console.error("Error fetching buildings:", error);
      }
    }
  }, []);

  // Fetch ClassRoom Data
  const fetchClassRoomData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/admin/classrooms/list`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const classroomData = data?.data || [];
      const updatedClassrooms = classroomData.map((room) => ({
        ...room,
        building: buildings.find((b) => b.id.toString() === room.building_id.toString()) || {
          name: "N/A",
        },
      }));
      setClasses(updatedClassrooms);
    } catch (error) {
      console.error("Error fetching classroom data:", error);
    } finally {
      setLoading(false);
    }
  }, [buildings]);

  useEffect(() => {
    fetchBuildingData();
  }, [fetchBuildingData]);

  useEffect(() => {
    if (buildings.length) fetchClassRoomData();
  }, [buildings, fetchClassRoomData]);

  // Handle Add Room Form Submit
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (parseInt(capacity) < 1) {
      setCapacityError("Capacity must be 1 or more.");
      return;
    }
    setCapacityError("");
    setLoading(true);
    try {
      const payload = {
        room_number: roomNumber.trim(),
        total_strength: Number(capacity),
        building_id: selectedBuilding,
      };
      const { status } = await axios.post(
        `${API_BASE_URL}/admin/classrooms/add`,
        payload,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (status === 201 || status === 200) {
        toggleModal();
        setRoomNumber("");
        setCapacity("");
        setSelectedBuilding("");

        toast?.current?.show({
          severity: "success",
          summary: "Added",
          detail: "Room added successfully",
          life: 3000,
        });

        fetchClassRoomData();
      }
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "error",
        detail: "Error adding room",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Add Building
  const handleAddBuilding = async () => {
    if (buildingName.trim() === "") return;
    setLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/admin/buildings/add`,
        { name: buildingName },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setBuildingName("");
      toggleBuildingModal();
      fetchBuildingData();
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "error",
        detail: "Error adding building",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Delete Classroom
  const handleDeleteClassroom = async () => {
    if (!classroomToDelete) return;

    setLoading(true);
    try {
      const { status } = await axios.delete(
        `${API_BASE_URL}/admin/classrooms/delete/${classroomToDelete.id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (status === 200) {
        toast.current.show({
          severity: "success",
          summary: "Deleted",
          detail: "Classroom deleted successfully",
          life: 3000,
        });
        fetchClassRoomData(); // Refresh the list of rooms
      }
    } catch (error) {
      console.error("Error deleting classroom:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Error deleting classroom",
        life: 3000,
      });
    } finally {
      setLoading(false);
      toggleDeleteModal(); // Close the modal
    }
  };

  // Handle Edit Classroom
  const handleEditClassroom = async (e) => {
    e.preventDefault();

    // Validate capacity
    if (parseInt(editCapacity) < 1) {
      setCapacityError("Capacity must be 1 or more.");
      return;
    }
    setCapacityError("");
    setLoading(true);

    try {
      // Prepare updated data
      const updatedRoomData = {
        id: editClassroomId,
        room_number: editRoomNumber.trim(),
        total_strength: Number(editCapacity),
        building_id: editSelectedBuilding,
      };

      // API call to update classroom
      const { status } = await axios.post(
        `${API_BASE_URL}/admin/classrooms/update/${editClassroomId}`,
        updatedRoomData,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      // If update is successful
      if (status === 200) {
        // Find the updated building
        const matchedBuilding = buildings.find(
          (b) => b.id.toString() === editSelectedBuilding.toString()
        );

        // Update the state with the updated classroom details
        setClasses((prevClassRooms) =>
          prevClassRooms.map((classroom) =>
            classroom.id === editClassroomId
              ? {
                  ...classroom,
                  room_number: editRoomNumber.trim(),
                  total_strength: Number(editCapacity),
                  building_id: editSelectedBuilding,
                  building: matchedBuilding || { name: "N/A" }, // Update the building field
                }
              : classroom
          )
        );

        toast.current.show({
          severity: "success",
          summary: "Updated",
          detail: "Classroom updated successfully",
          life: 3000,
        });
        setEditModal(false);
      }
    } catch (error) {
      console.error("Error updating classroom:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Error updating classrooms",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Rooms"
            breadcrumbItems={[
              { title: "School", link: "#" },
              { title: "Classes", link: "#" },
            ]}
          />
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p>Showing {classes.length} rooms</p>
            <Button
              type="button"
              color="primary"
              className="d-flex align-items-center gap-1"
              onClick={toggleModal}
            >
              <SquarePlus />
              <span>Add Room</span>
            </Button>
          </div>

          {/* Classroom Cards Section */}
          <Row>
            {loading ? (
              <Spinner color="primary" />
            ) : (
              classes.map((classroom) => (
                <ClassRoomCard
                  key={classroom.id}
                  classroom={classroom}
                  onEdit={() => toggleEditModal(classroom)}
                  onDelete={() => toggleDeleteModal(classroom)} // Open delete confirmation modal
                />
              ))
            )}
          </Row>
        </Container>
      </div>

      {/* Add Building Modal */}
      <Modal
        isOpen={buildingModal}
        toggle={toggleBuildingModal}
        centered
        backdrop="static"
      >
        <ModalHeader toggle={toggleBuildingModal}>Add Building</ModalHeader>
        <ModalBody>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleAddBuilding();
            }}
          >
            <FormGroup>
              <Label for="buildingName">Building Name</Label>
              <Input
                type="text"
                id="buildingName"
                value={buildingName}
                onChange={(e) => setBuildingName(e.target.value)}
                required
              />
            </FormGroup>
            <div className="text-end">
              <Button type="submit" color="primary">
                {loading ? <Spinner size="sm" /> : "Add Building"}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* Add Room Modal */}
      <Modal
        isOpen={modalCenter}
        toggle={toggleModal}
        centered
        backdrop="static"
      >
        <ModalHeader toggle={toggleModal}>Add Room</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleFormSubmit}>
            <Row>
              <Col md={6} sm={12} className="mb-3">
                <Label for="room_number">Room Number</Label>
                <Input
                  type="text"
                  id="room_number"
                  value={roomNumber}
                  onChange={(e) => setRoomNumber(e.target.value)}
                  required
                />
              </Col>
              <Col md={6} sm={12} className="mb-3">
                <Label for="capacity">Capacity</Label>
                <Input
                  type="number"
                  id="capacity"
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                  min="1"
                  required
                />
                {capacityError && (
                  <div className="text-danger mt-1">{capacityError}</div>
                )}
              </Col>
              <Col md={12} className="mb-3">
                <FormGroup>
                  <Label for="building">Building</Label>
                  <div className="d-flex justify-content-between gap-2">
                    <Input
                      type="select"
                      id="building"
                      value={selectedBuilding}
                      onChange={(e) => setSelectedBuilding(e.target.value)}
                      required
                    >
                      <option value="">Select a Building</option>
                      {buildings.map((building) => (
                        <option key={building.id} value={building.id}>
                          {building.name}
                        </option>
                      ))}
                    </Input>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={toggleBuildingModal}
                    >
                      <SquarePlus />
                    </Button>
                  </div>
                </FormGroup>
              </Col>
              <Col md={12} className="text-end">
                <Button type="submit" color="primary">
                  {loading ? <Spinner size="sm" /> : "Submit"}
                </Button>
              </Col>
            </Row>
          </Form>
        </ModalBody>
      </Modal>

      {/* Edit Room Modal */}
      <Modal
        isOpen={editModal}
        toggle={() => setEditModal(false)}
        centered
        backdrop="static"
      >
        <ModalHeader toggle={() => setEditModal(false)}>Edit Room</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleEditClassroom}>
            <Row>
              <Col md={6} sm={12} className="mb-3">
                <Label for="editRoomNumber">Room Number</Label>
                <Input
                  type="text"
                  id="editRoomNumber"
                  value={editRoomNumber}
                  onChange={(e) => setEditRoomNumber(e.target.value)}
                  required
                />
              </Col>
              <Col md={6} sm={12} className="mb-3">
                <Label for="editCapacity">Capacity</Label>
                <Input
                  type="number"
                  id="editCapacity"
                  value={editCapacity}
                  onChange={(e) => setEditCapacity(e.target.value)}
                  min="1"
                  required
                />
                {capacityError && (
                  <div className="text-danger mt-1">{capacityError}</div>
                )}
              </Col>
              <Col md={12} className="mb-3">
                <FormGroup>
                  <Label for="editBuilding">Building</Label>
                  <Input
                    type="select"
                    id="editBuilding"
                    value={editSelectedBuilding}
                    onChange={(e) => setEditSelectedBuilding(e.target.value)}
                    required
                  >
                    <option value="">Select a Building</option>
                    {buildings.map((building) => (
                      <option key={building.id} value={building.id}>
                        {building.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={12} className="text-end">
                <Button type="submit" color="primary">
                  {loading ? <Spinner size="sm" /> : "Update"}
                </Button>
              </Col>
            </Row>
          </Form>
        </ModalBody>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} centered toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
        <ModalBody>
          Are you sure you want to delete the classroom{" "}
          <strong>{classroomToDelete?.room_number}</strong>?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeleteClassroom}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>

      <Toast ref={toast} />
    </>
  );
};

export default ClassRooms;